// CB:BEGIN contractor-quote-bid-form
/**
 * Quote-style bid form for contractors: line items, homeowner/property (read-only),
 * material/warranty, scope notes. Ends with Final Bid Summary + Submit Bid + Save Draft.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';

const LINE_ITEM_CATEGORIES = ['MATERIAL', 'LABOR', 'DISPOSAL', 'PERMIT', 'SUPPLEMENT', 'OTHER'] as const;

/** Controlled dollar input that doesn't reformat while the user is typing */
function MoneyInput({ valueCents, onChangeCents, ...rest }: {
  valueCents: number | '';
  onChangeCents: (cents: number | '') => void;
  [k: string]: unknown;
}) {
  const [raw, setRaw] = React.useState(valueCents === '' ? '' : (Number(valueCents) / 100).toFixed(2));
  const focused = React.useRef(false);
  const prevCents = React.useRef(valueCents);

  // Sync external changes (e.g. auto-calc from qty × unit price) when not focused
  React.useEffect(() => {
    if (!focused.current && prevCents.current !== valueCents) {
      prevCents.current = valueCents;
      setRaw(valueCents === '' ? '' : (Number(valueCents) / 100).toFixed(2));
    }
  }, [valueCents]);

  return (
    <input
      {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
      type="text"
      inputMode="decimal"
      value={raw}
      onChange={(e) => {
        const v = e.target.value;
        setRaw(v);
        if (v.trim() === '' || v.trim() === '-' || v.trim() === '–') {
          prevCents.current = '';
          onChangeCents('');
          return;
        }
        const num = parseFloat(v.replace(/,/g, ''));
        if (!Number.isNaN(num) && num >= 0) {
          const cents = Math.round(num * 100);
          prevCents.current = cents;
          onChangeCents(cents);
        }
      }}
      onFocus={() => { focused.current = true; }}
      onBlur={() => {
        focused.current = false;
        const num = parseFloat(raw.replace(/,/g, ''));
        if (!Number.isNaN(num) && num >= 0) {
          const formatted = (Math.round(num * 100) / 100).toFixed(2);
          setRaw(formatted);
        } else if (raw.trim() === '') {
          setRaw('');
        }
      }}
    />
  );
}

interface LineItemRow {
  id: string;
  category: (typeof LINE_ITEM_CATEGORIES)[number];
  description: string;
  quantity: number | '';
  unit: string;
  unitPriceCents: number | '';
  totalCents: number | '';
}

function toDateStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

const defaultLineItem = (): LineItemRow => ({
  id: crypto.randomUUID(),
  category: 'LABOR',
  description: '',
  quantity: '',
  unit: 'EA',
  unitPriceCents: '',
  totalCents: '',
});

export interface QuoteBidPayload {
  amountCents: number;
  earliestStartDate: string;
  estimatedCompletionDate: string;
  note: string;
}

// CB:BEGIN contractor-template-types
export interface ContractorTemplateSummary {
  id: string;
  name: string;
  updatedAt: string;
}
// CB:END contractor-template-types

interface ContractorQuoteBidFormProps {
  project: {
    id: string;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    homeowner?: {
      firstName?: string | null;
      lastName?: string | null;
      user?: { email?: string | null; phone?: string | null } | null;
    } | null;
    roofReport?: {
      totalAreaSqFt?: number | null;
      totalSquares?: number | null;
      predominantPitch?: string | null;
      stories?: number | null;
      roofType?: string | null;
      suggestedWastePct?: number | null;
    } | null;
    [k: string]: unknown;
  };
  projectId: string;
  /** Contractor name, license, insurance, phone for prefill (from /api/contractors/self) */
  contractorInfo?: { company?: string | null; license?: string | null; insurance?: string | null; phone?: string | null } | null;
  onSubmitBid: (payload: QuoteBidPayload) => void;
  onSaveDraft: (payload: { amountCents?: number; note?: string; formData?: Record<string, unknown> }) => Promise<{ savedAt?: string } | void>;
  submitting?: boolean;
  disabled?: boolean;
  showPlatformFeeInfo?: boolean;
  onShowPlatformFeeInfo?: () => void;
  /** Prefill from draft: { amountCents?, note?, formData?, updatedAt? } */
  initialDraft?: { amountCents?: number | null; note?: string | null; formData?: Record<string, unknown> | null; updatedAt?: string | null } | null;
  // CB:BEGIN contractor-template-props
  /** Contractor's saved templates for this form type */
  templates?: ContractorTemplateSummary[];
  /** Fetch full template data by id */
  onLoadTemplate?: (id: string) => Promise<Record<string, unknown> | null>;
  /** Create a new named template from current form data (returns new template or null) */
  onSaveTemplate?: (name: string, data: Record<string, unknown>) => Promise<{ id: string; name: string } | null>;
  /** Delete a template by id */
  onDeleteTemplate?: (id: string) => Promise<void>;
  /** Refresh templates list after create/delete */
  onRefreshTemplates?: () => Promise<void>;
  // CB:END contractor-template-props
}

const ContractorQuoteBidForm: React.FC<ContractorQuoteBidFormProps> = ({
  project,
  projectId,
  contractorInfo,
  onSubmitBid,
  onSaveDraft,
  submitting = false,
  disabled = false,
  showPlatformFeeInfo = false,
  onShowPlatformFeeInfo,
  initialDraft,
  templates = [],
  onLoadTemplate,
  onSaveTemplate,
  onDeleteTemplate,
  onRefreshTemplates,
}) => {
  const now = new Date();
  const [issueDate, setIssueDate] = useState(toDateStr(now));
  const [expirationDate, setExpirationDate] = useState(toDateStr(new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)));
  const [earliestStartDate, setEarliestStartDate] = useState(toDateStr(now));
  const [estimatedCompletionDate, setEstimatedCompletionDate] = useState(toDateStr(new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000)));
  const [lineItems, setLineItems] = useState<LineItemRow[]>(() => [defaultLineItem(), defaultLineItem()]);
  const [materialType, setMaterialType] = useState('');
  // CB:BEGIN material-specification-state
  const [materialManufacturer, setMaterialManufacturer] = useState('');
  const [materialProductLine, setMaterialProductLine] = useState('');
  const [materialSpecUrl, setMaterialSpecUrl] = useState('');
  const [colorOptionsText, setColorOptionsText] = useState('');
  // CB:BEGIN structured-color-list
  const [colorList, setColorList] = useState<string[]>([]);
  const [newColorInput, setNewColorInput] = useState('');
  // CB:END structured-color-list
  // CB:END material-specification-state
  // Derived: color options from structured list (preferred) or legacy free-form text
  const colorOptions = React.useMemo(
    () => {
      const source = colorList.length > 0 ? colorList : colorOptionsText
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      return source.map((name) => ({ name, available: true }));
    },
    [colorList, colorOptionsText]
  );
  const [warrantyLaborYears, setWarrantyLaborYears] = useState<number | ''>('');
  const [warrantyMaterialYears, setWarrantyMaterialYears] = useState<number | ''>('');
  const [scopeNotes, setScopeNotes] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(initialDraft?.updatedAt ?? null);
  const draftAppliedRef = useRef(false);
  // CB:BEGIN contractor-template-state
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateNameInput, setTemplateNameInput] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  // CB:END contractor-template-state
  // Collapsible optional sections
  const [showRoofComponents, setShowRoofComponents] = useState(false);
  const [showAllowances, setShowAllowances] = useState(false);
  const [showOther, setShowOther] = useState(false);
  // CB:BEGIN contractor-quote-full-fields
  const [homeownerNameInput, setHomeownerNameInput] = useState('');
  const [propertyAddress1, setPropertyAddress1] = useState('');
  const [contractorName, setContractorName] = useState('');
  const [contractorLicense, setContractorLicense] = useState('');
  const [contractorInsurance, setContractorInsurance] = useState('');
  const [contractorPhone, setContractorPhone] = useState('');
  const [roofType, setRoofType] = useState('');
  const [roofSizeSqFt, setRoofSizeSqFt] = useState<number | ''>('');
  const [roofSizeSquares, setRoofSizeSquares] = useState<number | ''>('');
  const [roofPitch, setRoofPitch] = useState('');
  const [stories, setStories] = useState<number | ''>('');
  const [wasteFactorPct, setWasteFactorPct] = useState<number | ''>('');
  const [estimatedDurationDays, setEstimatedDurationDays] = useState<number | ''>('');
  const [exclusions, setExclusions] = useState('');
  const [systemShingles, setSystemShingles] = useState('');
  const [systemUnderlayment, setSystemUnderlayment] = useState('');
  const [systemStarter, setSystemStarter] = useState('');
  const [systemRidgeCap, setSystemRidgeCap] = useState('');
  const [systemLeakBarrier, setSystemLeakBarrier] = useState('');
  const [systemDripEdge, setSystemDripEdge] = useState('');
  const [systemVentilation, setSystemVentilation] = useState('');
  const [systemPipeBoots, setSystemPipeBoots] = useState('');
  const [systemDeckingAllowance, setSystemDeckingAllowance] = useState('');
  const [systemWarranties, setSystemWarranties] = useState('');
  const [allowanceExtraLayersCents, setAllowanceExtraLayersCents] = useState<number | ''>('');
  const [allowanceDeckingCents, setAllowanceDeckingCents] = useState<number | ''>('');
  const [allowanceRidgeVentCents, setAllowanceRidgeVentCents] = useState<number | ''>('');
  const [allowanceChimneyCricketCents, setAllowanceChimneyCricketCents] = useState<number | ''>('');
  const [manufacturerWarranty, setManufacturerWarranty] = useState('');
  const [workmanshipWarrantyText, setWorkmanshipWarrantyText] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [termsExclusions, setTermsExclusions] = useState('');
  const [materialsSummary, setMaterialsSummary] = useState('');
  const [ventilationSummary, setVentilationSummary] = useState('');
  // CB:END contractor-quote-full-fields

  // Prefill property/contractor/roof from project and contractorInfo (don't overwrite if draft already set)
  useEffect(() => {
    if (!project?.id) return;
    const name = [project?.homeowner?.firstName, project?.homeowner?.lastName].filter(Boolean).join(' ').trim();
    setHomeownerNameInput((prev) => prev || name || '');
    setPropertyAddress1((prev) => prev || (project.address ?? ''));
    setContractorName((prev) => prev || (contractorInfo?.company ?? ''));
    setContractorLicense((prev) => prev || (contractorInfo?.license ?? ''));
    setContractorInsurance((prev) => prev || (contractorInfo?.insurance ?? ''));
    setContractorPhone((prev) => prev || (contractorInfo?.phone ?? ''));
    const roof = project.roofReport;
    if (roof) {
      if (roof.totalAreaSqFt != null) {
        setRoofSizeSqFt((prev) => (prev === '' ? Number(roof.totalAreaSqFt) : prev));
        // Derive squares from sq ft using ceiling rule; override API value if sq ft is authoritative
        setRoofSizeSquares((prev) => (prev === '' ? Math.ceil(Number(roof.totalAreaSqFt) / 100) : prev));
      } else if (roof.totalSquares != null) {
        setRoofSizeSquares((prev) => (prev === '' ? Number(roof.totalSquares) : prev));
      }
      if (roof.predominantPitch) setRoofPitch((prev) => prev || (roof.predominantPitch as string));
      if (roof.stories != null) setStories((prev) => (prev === '' ? Number(roof.stories) : prev));
      if (roof.roofType) {
        const rt = roof.roofType as string;
        setRoofType((prev) => prev || (rt.length > 0 ? rt.charAt(0).toUpperCase() + rt.slice(1) : rt));
      }
      // CB:BEGIN roof-report-extended-metrics
      if (roof.suggestedWastePct != null) setWasteFactorPct((prev) => (prev === '' ? Number(roof.suggestedWastePct) : prev));
      // CB:END roof-report-extended-metrics
    }
  }, [project?.id, project?.address, project, project?.roofReport, contractorInfo?.company, contractorInfo?.license, contractorInfo?.insurance, contractorInfo?.phone]);

  // CB:BEGIN apply-form-data-helper
  /** Apply a data snapshot (from draft formData or template) to form state. */
  const applyFormData = useCallback((d: Record<string, unknown>, opts?: { skipProjectFields?: boolean }) => {
    const skip = opts?.skipProjectFields;
    if (d.lineItems && Array.isArray(d.lineItems) && (d.lineItems as unknown[]).length > 0) {
      setLineItems(
        (d.lineItems as Array<Record<string, unknown>>).map((r) => ({
          id: typeof r.id === 'string' ? r.id : crypto.randomUUID(),
          category: (r.category as LineItemRow['category']) || 'LABOR',
          description: String(r.description ?? ''),
          quantity: r.quantity != null ? Number(r.quantity) : '',
          unit: String(r.unit ?? 'EA'),
          unitPriceCents: r.unitPriceCents != null ? Number(r.unitPriceCents) : '',
          totalCents: r.totalCents != null ? Number(r.totalCents) : '',
        }))
      );
    }
    if (typeof d.earliestStartDate === 'string') setEarliestStartDate(d.earliestStartDate);
    if (typeof d.estimatedCompletionDate === 'string') setEstimatedCompletionDate(d.estimatedCompletionDate);
    if (typeof d.materialType === 'string') setMaterialType(d.materialType);
    if (typeof d.materialManufacturer === 'string') setMaterialManufacturer(d.materialManufacturer);
    if (typeof d.materialProductLine === 'string') setMaterialProductLine(d.materialProductLine);
    if (typeof d.materialSpecUrl === 'string') setMaterialSpecUrl(d.materialSpecUrl);
    if (typeof d.scopeNotes === 'string') setScopeNotes(d.scopeNotes);
    if (d.warrantyLaborYears != null) setWarrantyLaborYears(Number(d.warrantyLaborYears));
    if (d.warrantyMaterialYears != null) setWarrantyMaterialYears(Number(d.warrantyMaterialYears));
    if (typeof d.additionalNotes === 'string') setAdditionalNotes(d.additionalNotes);
    if (typeof d.exclusions === 'string') setExclusions(d.exclusions);
    if (typeof d.systemShingles === 'string') setSystemShingles(d.systemShingles);
    if (typeof d.systemUnderlayment === 'string') setSystemUnderlayment(d.systemUnderlayment);
    if (typeof d.systemStarter === 'string') setSystemStarter(d.systemStarter);
    if (typeof d.systemRidgeCap === 'string') setSystemRidgeCap(d.systemRidgeCap);
    if (typeof d.systemLeakBarrier === 'string') setSystemLeakBarrier(d.systemLeakBarrier);
    if (typeof d.systemDripEdge === 'string') setSystemDripEdge(d.systemDripEdge);
    if (typeof d.systemVentilation === 'string') setSystemVentilation(d.systemVentilation);
    if (typeof d.systemPipeBoots === 'string') setSystemPipeBoots(d.systemPipeBoots);
    if (typeof d.systemDeckingAllowance === 'string') setSystemDeckingAllowance(d.systemDeckingAllowance);
    if (typeof d.systemWarranties === 'string') setSystemWarranties(d.systemWarranties);
    if (d.allowanceExtraLayersCents != null) setAllowanceExtraLayersCents(Number(d.allowanceExtraLayersCents));
    if (d.allowanceDeckingCents != null) setAllowanceDeckingCents(Number(d.allowanceDeckingCents));
    if (d.allowanceRidgeVentCents != null) setAllowanceRidgeVentCents(Number(d.allowanceRidgeVentCents));
    if (d.allowanceChimneyCricketCents != null) setAllowanceChimneyCricketCents(Number(d.allowanceChimneyCricketCents));
    if (typeof d.manufacturerWarranty === 'string') setManufacturerWarranty(d.manufacturerWarranty);
    if (typeof d.workmanshipWarranty === 'string') setWorkmanshipWarrantyText(d.workmanshipWarranty);
    if (typeof d.termsAndConditions === 'string') setTermsAndConditions(d.termsAndConditions);
    if (typeof d.termsExclusions === 'string') setTermsExclusions(d.termsExclusions);
    if (typeof d.materialsSummary === 'string') setMaterialsSummary(d.materialsSummary);
    if (typeof d.ventilationSummary === 'string') setVentilationSummary(d.ventilationSummary);
    if (d.estimatedDurationDays != null) setEstimatedDurationDays(Number(d.estimatedDurationDays));
    if (d.wasteFactorPct != null) setWasteFactorPct(Number(d.wasteFactorPct));
    if (d.availableColors && Array.isArray(d.availableColors)) {
      const names = (d.availableColors as Array<{ name?: string }>).map((c) =>
        c && typeof c.name === 'string' ? c.name : String(c)
      ).filter(Boolean);
      // CB:BEGIN structured-color-list-draft — populate structured list from draft data
      setColorList(names);
      setColorOptionsText(names.join('\n'));
      // CB:END structured-color-list-draft
    }
    // Project-specific fields — included for drafts but skipped when loading a template
    if (!skip) {
      if (typeof d.homeownerName === 'string') setHomeownerNameInput(d.homeownerName);
      if (typeof d.propertyAddress1 === 'string') setPropertyAddress1(d.propertyAddress1);
      if (typeof d.contractorName === 'string') setContractorName(d.contractorName);
      if (typeof d.contractorLicense === 'string') setContractorLicense(d.contractorLicense);
      if (typeof d.contractorInsurance === 'string') setContractorInsurance(d.contractorInsurance);
      if (typeof d.roofType === 'string') setRoofType(d.roofType);
      if (d.roofSizeSqFt != null) setRoofSizeSqFt(Number(d.roofSizeSqFt));
      if (d.roofSizeSquares != null) setRoofSizeSquares(Number(d.roofSizeSquares));
      if (typeof d.roofPitch === 'string') setRoofPitch(d.roofPitch);
      if (d.stories != null) setStories(Number(d.stories));
    }
  }, []);
  // CB:END apply-form-data-helper

  // Prefill from draft once when loaded — use structured formData if available, fall back to parsing note.
  useEffect(() => {
    if (!initialDraft || draftAppliedRef.current) return;
    draftAppliedRef.current = true;
    if (initialDraft.formData && typeof initialDraft.formData === 'object') {
      applyFormData(initialDraft.formData as Record<string, unknown>);
      return;
    }
    // Legacy: note stored as JSON string
    if (typeof initialDraft.amountCents === 'number' && initialDraft.amountCents > 0 && (!initialDraft.note || !String(initialDraft.note).trim())) {
      setLineItems([{ ...defaultLineItem(), totalCents: initialDraft.amountCents, unitPriceCents: initialDraft.amountCents, description: 'Bid total' }]);
    }
    if (typeof initialDraft.note === 'string' && initialDraft.note.trim()) {
      try {
        applyFormData(JSON.parse(initialDraft.note) as Record<string, unknown>);
      } catch {
        setAdditionalNotes(initialDraft.note);
      }
    }
  }, [initialDraft, applyFormData]);

  const updateLineItem = useCallback((id: string, field: keyof LineItemRow, value: string | number) => {
    setLineItems((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, [field]: value };
        if (field === 'quantity' || field === 'unitPriceCents') {
          const q = field === 'quantity' ? (typeof value === 'number' ? value : Number(value)) : r.quantity;
          const up = field === 'unitPriceCents' ? (typeof value === 'number' ? value : Number(value)) : r.unitPriceCents;
          if (typeof q === 'number' && !Number.isNaN(q) && typeof up === 'number' && !Number.isNaN(up)) {
            next.totalCents = Math.round(q * up);
          }
        }
        if (field === 'totalCents' && typeof value === 'number' && !Number.isNaN(value)) {
          next.totalCents = value;
        }
        return next;
      })
    );
  }, []);

  const addLineItem = useCallback(() => {
    setLineItems((prev) => [...prev, defaultLineItem()]);
  }, []);

  const removeLineItem = useCallback((id: string) => {
    setLineItems((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  }, []);

  const subtotalCents = lineItems.reduce((sum, r) => sum + (r.totalCents === '' ? 0 : Number(r.totalCents)), 0);
  const effectiveBidCents = subtotalCents;
  const platformFeeCents = Math.round(effectiveBidCents * 0.1);
  const totalAmountCents = effectiveBidCents + platformFeeCents;
  const effectiveBid = effectiveBidCents / 100;
  const platformFee = platformFeeCents / 100;
  const totalAmount = totalAmountCents / 100;

  const buildNoteJson = useCallback((): string => {
    const payload = {
      lineItems: lineItems.map((r) => ({
        id: r.id,
        category: r.category,
        description: r.description,
        quantity: r.quantity === '' ? null : Number(r.quantity),
        unit: r.unit || undefined,
        unitPriceCents: r.unitPriceCents === '' ? null : Number(r.unitPriceCents),
        totalCents: r.totalCents === '' ? null : Number(r.totalCents),
      })),
      issueDate,
      expirationDate,
      earliestStartDate,
      estimatedCompletionDate,
      homeownerName: homeownerNameInput || undefined,
      propertyAddress1: propertyAddress1 || undefined,
      contractorName: contractorName || undefined,
      contractorLicense: contractorLicense || undefined,
      contractorInsurance: contractorInsurance || undefined,
      roofType: roofType || undefined,
      roofSizeSqFt: roofSizeSqFt === '' ? undefined : Number(roofSizeSqFt),
      roofSizeSquares: roofSizeSquares === '' ? undefined : Number(roofSizeSquares),
      roofPitch: roofPitch || undefined,
      stories: stories === '' ? undefined : Number(stories),
      wasteFactorPct: wasteFactorPct === '' ? undefined : Number(wasteFactorPct),
      estimatedDurationDays: estimatedDurationDays === '' ? undefined : Number(estimatedDurationDays),
      exclusions: exclusions || undefined,
      systemShingles: systemShingles || undefined,
      systemUnderlayment: systemUnderlayment || undefined,
      systemStarter: systemStarter || undefined,
      systemRidgeCap: systemRidgeCap || undefined,
      systemLeakBarrier: systemLeakBarrier || undefined,
      systemDripEdge: systemDripEdge || undefined,
      systemVentilation: systemVentilation || undefined,
      systemPipeBoots: systemPipeBoots || undefined,
      systemDeckingAllowance: systemDeckingAllowance || undefined,
      systemWarranties: systemWarranties || undefined,
      allowanceExtraLayersCents: allowanceExtraLayersCents === '' ? undefined : Number(allowanceExtraLayersCents),
      allowanceDeckingCents: allowanceDeckingCents === '' ? undefined : Number(allowanceDeckingCents),
      allowanceRidgeVentCents: allowanceRidgeVentCents === '' ? undefined : Number(allowanceRidgeVentCents),
      allowanceChimneyCricketCents: allowanceChimneyCricketCents === '' ? undefined : Number(allowanceChimneyCricketCents),
      manufacturerWarranty: manufacturerWarranty || undefined,
      workmanshipWarranty: workmanshipWarrantyText || undefined,
      termsAndConditions: termsAndConditions || undefined,
      termsExclusions: termsExclusions || undefined,
      materialsSummary: materialsSummary || undefined,
      ventilationSummary: ventilationSummary || undefined,
      materialType: materialType || undefined,
      materialManufacturer: materialManufacturer || undefined,
      materialProductLine: materialProductLine || undefined,
      materialSpecUrl: materialSpecUrl || undefined,
      availableColors: colorOptions.length > 0 ? colorOptions : undefined,
      warrantyLaborYears: warrantyLaborYears === '' ? undefined : Number(warrantyLaborYears),
      warrantyMaterialYears: warrantyMaterialYears === '' ? undefined : Number(warrantyMaterialYears),
      scopeNotes: scopeNotes || undefined,
      additionalNotes: additionalNotes || undefined,
      contractorPhone: contractorPhone || undefined,
    };
    return JSON.stringify(payload);
  }, [lineItems, issueDate, expirationDate, earliestStartDate, estimatedCompletionDate, homeownerNameInput, propertyAddress1, contractorName, contractorLicense, contractorInsurance, contractorPhone, roofType, roofSizeSqFt, roofSizeSquares, roofPitch, stories, wasteFactorPct, estimatedDurationDays, exclusions, systemShingles, systemUnderlayment, systemStarter, systemRidgeCap, systemLeakBarrier, systemDripEdge, systemVentilation, systemPipeBoots, systemDeckingAllowance, systemWarranties, allowanceExtraLayersCents, allowanceDeckingCents, allowanceRidgeVentCents, allowanceChimneyCricketCents, manufacturerWarranty, workmanshipWarrantyText, termsAndConditions, termsExclusions, materialsSummary, ventilationSummary, materialType, materialManufacturer, materialProductLine, materialSpecUrl, colorOptions, warrantyLaborYears, warrantyMaterialYears, scopeNotes, additionalNotes]);

  // CB:BEGIN contractor-template-handlers
  /** Extracts non-project-specific fields to store in a reusable template. */
  const buildTemplateData = useCallback((): Record<string, unknown> => ({
    lineItems: lineItems.map((r) => ({
      id: r.id,
      category: r.category,
      description: r.description,
      quantity: r.quantity === '' ? null : Number(r.quantity),
      unit: r.unit || 'EA',
      unitPriceCents: r.unitPriceCents === '' ? null : Number(r.unitPriceCents),
      totalCents: r.totalCents === '' ? null : Number(r.totalCents),
    })),
    systemShingles: systemShingles || undefined,
    systemUnderlayment: systemUnderlayment || undefined,
    systemStarter: systemStarter || undefined,
    systemRidgeCap: systemRidgeCap || undefined,
    systemLeakBarrier: systemLeakBarrier || undefined,
    systemDripEdge: systemDripEdge || undefined,
    systemVentilation: systemVentilation || undefined,
    systemPipeBoots: systemPipeBoots || undefined,
    systemDeckingAllowance: systemDeckingAllowance || undefined,
    systemWarranties: systemWarranties || undefined,
    scopeNotes: scopeNotes || undefined,
    exclusions: exclusions || undefined,
    manufacturerWarranty: manufacturerWarranty || undefined,
    workmanshipWarranty: workmanshipWarrantyText || undefined,
    termsAndConditions: termsAndConditions || undefined,
    termsExclusions: termsExclusions || undefined,
    materialsSummary: materialsSummary || undefined,
    ventilationSummary: ventilationSummary || undefined,
    materialType: materialType || undefined,
    warrantyLaborYears: warrantyLaborYears === '' ? undefined : Number(warrantyLaborYears),
    warrantyMaterialYears: warrantyMaterialYears === '' ? undefined : Number(warrantyMaterialYears),
    estimatedDurationDays: estimatedDurationDays === '' ? undefined : Number(estimatedDurationDays),
    wasteFactorPct: wasteFactorPct === '' ? undefined : Number(wasteFactorPct),
    allowanceExtraLayersCents: allowanceExtraLayersCents === '' ? undefined : Number(allowanceExtraLayersCents),
    allowanceDeckingCents: allowanceDeckingCents === '' ? undefined : Number(allowanceDeckingCents),
    allowanceRidgeVentCents: allowanceRidgeVentCents === '' ? undefined : Number(allowanceRidgeVentCents),
    allowanceChimneyCricketCents: allowanceChimneyCricketCents === '' ? undefined : Number(allowanceChimneyCricketCents),
    additionalNotes: additionalNotes || undefined,
  }), [lineItems, systemShingles, systemUnderlayment, systemStarter, systemRidgeCap, systemLeakBarrier, systemDripEdge, systemVentilation, systemPipeBoots, systemDeckingAllowance, systemWarranties, scopeNotes, exclusions, manufacturerWarranty, workmanshipWarrantyText, termsAndConditions, termsExclusions, materialsSummary, ventilationSummary, materialType, warrantyLaborYears, warrantyMaterialYears, estimatedDurationDays, wasteFactorPct, allowanceExtraLayersCents, allowanceDeckingCents, allowanceRidgeVentCents, allowanceChimneyCricketCents, additionalNotes]);

  const handleApplyTemplate = useCallback(async () => {
    if (!selectedTemplateId || !onLoadTemplate) return;
    setApplyingTemplate(true);
    try {
      const tpl = await onLoadTemplate(selectedTemplateId);
      if (tpl?.data && typeof tpl.data === 'object') {
        applyFormData(tpl.data as Record<string, unknown>, { skipProjectFields: true });
      } else if (tpl && typeof tpl === 'object') {
        applyFormData(tpl as Record<string, unknown>, { skipProjectFields: true });
      }
    } finally {
      setApplyingTemplate(false);
    }
  }, [selectedTemplateId, onLoadTemplate, applyFormData]);

  const handleConfirmSaveTemplate = useCallback(async () => {
    const name = templateNameInput.trim();
    if (!name || !onSaveTemplate) return;
    setSavingTemplate(true);
    try {
      await onSaveTemplate(name, buildTemplateData());
      await onRefreshTemplates?.();
      setShowSaveTemplateModal(false);
      setTemplateNameInput('');
    } finally {
      setSavingTemplate(false);
    }
  }, [templateNameInput, onSaveTemplate, buildTemplateData, onRefreshTemplates]);
  // CB:END contractor-template-handlers

  const handleSubmit = () => {
    if (subtotalCents <= 0) {
      alert('Please add at least one line item with a total amount.');
      return;
    }
    if (!materialType || !materialType.trim()) {
      alert('Please enter the type of material.');
      return;
    }
    if (!materialManufacturer) {
      alert('Please select a manufacturer.');
      return;
    }
    if (!materialProductLine.trim()) {
      alert('Please enter the product line.');
      return;
    }
    if (!materialSpecUrl.trim()) {
      alert('Please enter the product specification URL.');
      return;
    }
    if (colorList.length === 0) {
      alert('Please add at least one color option for the homeowner to choose from.');
      return;
    }
    if (!earliestStartDate) {
      alert('Please enter an earliest start date.');
      return;
    }
    if (!estimatedCompletionDate) {
      alert('Please enter an estimated completion date.');
      return;
    }
    if (estimatedDurationDays === '' || estimatedDurationDays == null) {
      alert('Please enter an estimated duration in days.');
      return;
    }
    onSubmitBid({
      amountCents: subtotalCents,
      earliestStartDate,
      estimatedCompletionDate,
      note: buildNoteJson(),
    });
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const noteJson = buildNoteJson();
      const result = await onSaveDraft({
        amountCents: subtotalCents > 0 ? subtotalCents : undefined,
        note: noteJson,
        formData: JSON.parse(noteJson) as Record<string, unknown>,
      });
      const savedAt = (result as any)?.savedAt;
      if (savedAt) setDraftSavedAt(savedAt);
      else setDraftSavedAt(new Date().toISOString());
    } finally {
      setSavingDraft(false);
    }
  };

  const taxAndFeesCents = Math.round(subtotalCents * 0.1);
  const totalCents = subtotalCents + taxAndFeesCents;

  return (
    <div className="dashboard-card">
      <h3 style={{ marginBottom: 16 }}>Create Quote</h3>
      <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>Fill required sections and at least one line item. Amounts stored as cents.</p>

      {/* Quote Information (Required) */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Quote Information (Required)</h4>
        {/* CB:BEGIN draft-status-row */}
        <div className="form-group" style={{ marginBottom: 12 }}>
          <label>Draft</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {draftSavedAt ? (
              <span style={{ fontSize: 13, color: '#059669', fontWeight: 500 }}>
                ✓ Draft saved {new Date(draftSavedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            ) : (
              <span style={{ fontSize: 13, color: '#6b7280' }}>No draft saved yet — use &quot;Save Draft&quot; below.</span>
            )}
          </div>
        </div>
        {/* CB:END draft-status-row */}
        {/* CB:BEGIN templates-ui */}
        <div className="form-group" style={{ marginBottom: 12 }}>
          <label>Templates</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {templates.length > 0 ? (
              <select
                className="form-control"
                style={{ maxWidth: 240 }}
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
              >
                <option value="">— Select a template —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            ) : (
              <span style={{ fontSize: 13, color: '#6b7280' }}>No saved templates yet.</span>
            )}
            {templates.length > 0 && (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={!selectedTemplateId || applyingTemplate}
                onClick={handleApplyTemplate}
              >
                {applyingTemplate ? 'Loading…' : 'Apply Template'}
              </button>
            )}
            {onSaveTemplate && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setTemplateNameInput(''); setShowSaveTemplateModal(true); }}
              >
                Save as Template
              </button>
            )}
            {onDeleteTemplate && selectedTemplateId && (
              <button
                type="button"
                className="btn btn-danger"
                style={{ fontSize: 12, padding: '4px 10px' }}
                onClick={async () => {
                  if (!window.confirm('Delete this template?')) return;
                  await onDeleteTemplate(selectedTemplateId);
                  setSelectedTemplateId('');
                  await onRefreshTemplates?.();
                }}
              >
                Delete
              </button>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            Templates save your line items, materials, scope, terms & conditions — not project-specific data.
          </div>
        </div>
        {/* Save as Template modal (inline) */}
        {showSaveTemplateModal && (
          <div style={{ background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: 8, padding: 16, marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Name this template</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                className="form-control"
                style={{ maxWidth: 280 }}
                type="text"
                placeholder="e.g. Standard Asphalt — 3 Story"
                value={templateNameInput}
                onChange={(e) => setTemplateNameInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmSaveTemplate(); if (e.key === 'Escape') setShowSaveTemplateModal(false); }}
                autoFocus
              />
              <button
                type="button"
                className="btn btn-success"
                disabled={!templateNameInput.trim() || savingTemplate}
                onClick={handleConfirmSaveTemplate}
              >
                {savingTemplate ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowSaveTemplateModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {/* CB:END templates-ui */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label>Issue date <span style={{ color: '#ef4444' }}>*</span></label>
            <input className="form-control" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Expiration date <span style={{ color: '#ef4444' }}>*</span></label>
            <input className="form-control" type="date" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Homeowner & Property (Required) — prefill from project */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Homeowner & Property (Required)</h4>
        <div className="form-group">
          <label>Homeowner name <span style={{ color: '#ef4444' }}>*</span></label>
          <input className="form-control" type="text" value={homeownerNameInput} onChange={(e) => setHomeownerNameInput(e.target.value)} placeholder="Homeowner name" />
        </div>
        <div className="form-group">
          <label>Address line 1 <span style={{ color: '#ef4444' }}>*</span></label>
          <input className="form-control" type="text" value={propertyAddress1} onChange={(e) => setPropertyAddress1(e.target.value)} />
        </div>
      </div>

      {/* Contractor (Required) — name editable; license and insurance from DB */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Contractor (Required)</h4>
        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Only contractor name is shown on the homeowner-facing PDF. License and insurance are loaded from your profile.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label>Contractor name <span style={{ color: '#ef4444' }}>*</span></label>
            <input className="form-control" type="text" value={contractorName} onChange={(e) => setContractorName(e.target.value)} placeholder="Your business name" />
          </div>
          <div className="form-group">
            <label>License number <span style={{ color: '#ef4444' }}>*</span></label>
            <input className="form-control" type="text" value={contractorLicense} onChange={(e) => setContractorLicense(e.target.value)} placeholder="License number" />
          </div>
          <div className="form-group">
            <label>Insurance <span style={{ color: '#ef4444' }}>*</span></label>
            <input className="form-control" type="text" value={contractorInsurance} onChange={(e) => setContractorInsurance(e.target.value)} placeholder="Insurance" />
          </div>
        </div>
      </div>

      {/* Roof Measurements (Required) — report links are in project card Show More above */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Roof Measurements (Required)</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label>Roof type <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              className="form-control"
              type="text"
              value={roofType}
              onChange={(e) => {
                const v = e.target.value;
                setRoofType(v.length > 0 ? v.charAt(0).toUpperCase() + v.slice(1) : v);
              }}
              placeholder="e.g., Asphalt shingle"
            />
          </div>
          <div className="form-group">
            <label>Roof size (sq ft or squares) <span style={{ color: '#ef4444' }}>*</span></label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="form-control"
                type="text"
                inputMode="decimal"
                placeholder="Sq ft"
                value={roofSizeSqFt === '' ? '' : roofSizeSqFt}
                onChange={(e) => {
                  const v = e.target.value === '' ? '' : Number(e.target.value);
                  setRoofSizeSqFt(v);
                  if (v !== '' && !Number.isNaN(v) && v > 0) {
                    setRoofSizeSquares(Math.ceil(v / 100));
                  } else if (v === '') {
                    setRoofSizeSquares('');
                  }
                }}
              />
              <input className="form-control" type="text" inputMode="decimal" placeholder="Squares" value={roofSizeSquares === '' ? '' : roofSizeSquares} onChange={(e) => setRoofSizeSquares(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          </div>
          <div className="form-group">
            <label>Pitch (optional)</label>
            <input className="form-control" type="text" value={roofPitch} onChange={(e) => setRoofPitch(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Stories (optional)</label>
            <input className="form-control" type="number" value={stories === '' ? '' : stories} onChange={(e) => setStories(e.target.value === '' ? '' : Number(e.target.value))} />
          </div>
          <div className="form-group">
            <label>Waste factor % (optional)</label>
            <input className="form-control" type="text" inputMode="decimal" value={wasteFactorPct === '' ? '' : wasteFactorPct} onChange={(e) => setWasteFactorPct(e.target.value === '' ? '' : Number(e.target.value))} />
          </div>
          <div className="form-group">
            <label>Estimated duration (days) <span style={{ color: '#ef4444' }}>*</span></label>
            <input className="form-control" type="number" required value={estimatedDurationDays === '' ? '' : estimatedDurationDays} onChange={(e) => setEstimatedDurationDays(e.target.value === '' ? '' : Number(e.target.value))} />
          </div>
        </div>
        <div className="form-group">
          <label>Exclusions (optional)</label>
          <textarea className="form-control" rows={2} value={exclusions} onChange={(e) => setExclusions(e.target.value)} />
        </div>
      </div>

      {/* Roof System Components (optional) — collapsible */}
      <div style={{ marginBottom: 24, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <button type="button" onClick={() => setShowRoofComponents(v => !v)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f9fafb', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>Roof System Components <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 13 }}>(optional)</span></span>
          <span style={{ fontSize: 18, color: '#6b7280', lineHeight: 1 }}>{showRoofComponents ? '▲' : '▼'}</span>
        </button>
        {showRoofComponents && (
          <div style={{ padding: '16px' }}>
            <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Informational specs only; pricing is in Itemized Pricing below.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group"><label>Shingles</label><input className="form-control" type="text" value={systemShingles} onChange={(e) => setSystemShingles(e.target.value)} /></div>
              <div className="form-group"><label>Underlayment</label><input className="form-control" type="text" value={systemUnderlayment} onChange={(e) => setSystemUnderlayment(e.target.value)} /></div>
              <div className="form-group"><label>Starter</label><input className="form-control" type="text" value={systemStarter} onChange={(e) => setSystemStarter(e.target.value)} /></div>
              <div className="form-group"><label>Ridge cap</label><input className="form-control" type="text" value={systemRidgeCap} onChange={(e) => setSystemRidgeCap(e.target.value)} /></div>
              <div className="form-group"><label>Leak barrier</label><input className="form-control" type="text" value={systemLeakBarrier} onChange={(e) => setSystemLeakBarrier(e.target.value)} /></div>
              <div className="form-group"><label>Drip edge</label><input className="form-control" type="text" value={systemDripEdge} onChange={(e) => setSystemDripEdge(e.target.value)} /></div>
              <div className="form-group"><label>Ventilation</label><input className="form-control" type="text" value={systemVentilation} onChange={(e) => setSystemVentilation(e.target.value)} /></div>
              <div className="form-group"><label>Pipe boots</label><input className="form-control" type="text" value={systemPipeBoots} onChange={(e) => setSystemPipeBoots(e.target.value)} /></div>
              <div className="form-group"><label>Decking allowance</label><input className="form-control" type="text" value={systemDeckingAllowance} onChange={(e) => setSystemDeckingAllowance(e.target.value)} /></div>
              <div className="form-group"><label>Warranties (spec)</label><input className="form-control" type="text" value={systemWarranties} onChange={(e) => setSystemWarranties(e.target.value)} /></div>
            </div>
          </div>
        )}
      </div>

      {/* Allowances & Unit Rates (Optional) — collapsible */}
      <div style={{ marginBottom: 24, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <button type="button" onClick={() => setShowAllowances(v => !v)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f9fafb', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>Allowances & Unit Rates <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 13 }}>(optional)</span></span>
          <span style={{ fontSize: 18, color: '#6b7280', lineHeight: 1 }}>{showAllowances ? '▲' : '▼'}</span>
        </button>
        {showAllowances && (
          <div style={{ padding: '16px' }}>
            <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Values in cents (e.g. 1500 = $15.00).</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label>Extra layers ($/100 sq ft, cents)</label>
                <input className="form-control" type="text" inputMode="numeric" value={allowanceExtraLayersCents === '' ? '' : allowanceExtraLayersCents} onChange={(e) => setAllowanceExtraLayersCents(e.target.value === '' ? '' : Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Decking ($/sheet, cents)</label>
                <input className="form-control" type="text" inputMode="numeric" value={allowanceDeckingCents === '' ? '' : allowanceDeckingCents} onChange={(e) => setAllowanceDeckingCents(e.target.value === '' ? '' : Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Ridge vent ($/lf, cents)</label>
                <input className="form-control" type="text" inputMode="numeric" value={allowanceRidgeVentCents === '' ? '' : allowanceRidgeVentCents} onChange={(e) => setAllowanceRidgeVentCents(e.target.value === '' ? '' : Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Chimney cricket ($/chimney, cents)</label>
                <input className="form-control" type="text" inputMode="numeric" value={allowanceChimneyCricketCents === '' ? '' : allowanceChimneyCricketCents} onChange={(e) => setAllowanceChimneyCricketCents(e.target.value === '' ? '' : Number(e.target.value))} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Itemized Pricing (Required) */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Itemized Pricing (Required)</h4>
        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Each line item: description required. At least one must have a price (Total $ &gt; 0). Use – or leave Total empty for no price.</p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '8px 6px' }}>Category</th>
                <th style={{ padding: '8px 6px' }}>Description</th>
                <th style={{ padding: '8px 6px', width: 70 }}>Qty</th>
                <th style={{ padding: '8px 6px', width: 60 }}>Unit</th>
                <th style={{ padding: '8px 6px', width: 80 }}>Unit $</th>
                <th style={{ padding: '8px 6px', width: 90 }}>Total $</th>
                <th style={{ padding: '8px 6px', width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((row) => (
                <tr key={row.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '6px' }}>
                    <select
                      className="form-control"
                      style={{ minWidth: 100 }}
                      value={row.category}
                      onChange={(e) => updateLineItem(row.id, 'category', e.target.value as LineItemRow['category'])}
                    >
                      {LINE_ITEM_CATEGORIES.map((c) => (
                        <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '6px' }}>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Description"
                      value={row.description}
                      onChange={(e) => updateLineItem(row.id, 'description', e.target.value)}
                    />
                  </td>
                  <td style={{ padding: '6px' }}>
                    <input
                      className="form-control"
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      value={row.quantity === '' ? '' : row.quantity}
                      onChange={(e) => {
                        const v = e.target.value.trim();
                        updateLineItem(row.id, 'quantity', v === '' ? '' : Number(v));
                      }}
                    />
                  </td>
                  <td style={{ padding: '6px' }}>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="EA"
                      value={row.unit}
                      onChange={(e) => updateLineItem(row.id, 'unit', e.target.value)}
                    />
                  </td>
                  <td style={{ padding: '6px' }}>
                    <MoneyInput
                      className="form-control"
                      placeholder="0.00"
                      valueCents={row.unitPriceCents}
                      onChangeCents={(v) => updateLineItem(row.id, 'unitPriceCents', v === '' ? '' : v)}
                    />
                  </td>
                  <td style={{ padding: '6px' }}>
                    <MoneyInput
                      className="form-control"
                      placeholder="0.00"
                      valueCents={row.totalCents}
                      onChangeCents={(v) => updateLineItem(row.id, 'totalCents', v === '' ? '' : v)}
                    />
                  </td>
                  <td style={{ padding: '6px' }}>
                    <button type="button" className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: 12 }} onClick={() => removeLineItem(row.id)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button type="button" className="btn btn-secondary" style={{ marginTop: 12 }} onClick={addLineItem}>
          + Add Line Item
        </button>
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb', display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
          <span style={{ fontSize: 14, color: '#6b7280' }}>Subtotal: ${(subtotalCents / 100).toFixed(2)}</span>
          <span style={{ fontSize: 14, color: '#6b7280' }}>Tax and fees: ${(taxAndFeesCents / 100).toFixed(2)}</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Total: ${(totalCents / 100).toFixed(2)}</span>
        </div>
      </div>

      {/* Other (Optional) — collapsible */}
      <div style={{ marginBottom: 24, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <button type="button" onClick={() => setShowOther(v => !v)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f9fafb', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>Other <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 13 }}>(optional)</span></span>
          <span style={{ fontSize: 18, color: '#6b7280', lineHeight: 1 }}>{showOther ? '▲' : '▼'}</span>
        </button>
        {showOther && (
          <div style={{ padding: '16px' }}>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Materials summary</label>
              <textarea className="form-control" rows={2} value={materialsSummary} onChange={(e) => setMaterialsSummary(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Ventilation summary</label>
              <textarea className="form-control" rows={2} value={ventilationSummary} onChange={(e) => setVentilationSummary(e.target.value)} />
            </div>
          </div>
        )}
      </div>

      {/* Schedule */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Schedule</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label>Earliest Start Date <span style={{ color: '#ef4444' }}>*</span></label>
            <input className="form-control" type="date" required value={earliestStartDate} min={toDateStr(now)} onChange={(e) => setEarliestStartDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Estimated Completion Date <span style={{ color: '#ef4444' }}>*</span></label>
            <input className="form-control" type="date" required value={estimatedCompletionDate} onChange={(e) => setEstimatedCompletionDate(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Material & Warranty — single warranty section (years + optional text) */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Material & Warranty</h4>
        <div className="form-group">
          <label>Type of Material <span style={{ color: '#ef4444' }}>*</span></label>
          {/* CB:BEGIN material-type-select */}
          <select
            className="form-control"
            required
            value={materialType}
            onChange={(e) => setMaterialType(e.target.value)}
          >
            <option value="">Select material type…</option>
            <option value="Architectural Asphalt Shingles">Architectural Asphalt Shingles</option>
            <option value="3-Tab Asphalt Shingles">3-Tab Asphalt Shingles</option>
            <option value="Impact-Resistant Shingles">Impact-Resistant Shingles</option>
            <option value="Metal Roofing">Metal Roofing</option>
            <option value="Cedar Shake">Cedar Shake</option>
            <option value="Flat / TPO / EPDM">Flat / TPO / EPDM</option>
            <option value="Tile">Tile</option>
            <option value="Slate">Slate</option>
            <option value="Other">Other</option>
          </select>
          {materialType === 'Other' && (
            <input
              className="form-control"
              type="text"
              placeholder="Describe material type"
              style={{ marginTop: 6 }}
              onChange={(e) => setMaterialType(e.target.value === 'Other' ? 'Other' : e.target.value)}
            />
          )}
          {/* CB:END material-type-select */}
        </div>

        {/* CB:BEGIN material-specification-fields */}
        <div style={{ marginTop: 12, padding: '12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6 }}>
          <h5 style={{ marginBottom: 12, fontSize: 14, fontWeight: 600, color: '#374151' }}>Material Specification (Required)</h5>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div className="form-group">
              <label>Manufacturer <span style={{ color: '#ef4444' }}>*</span></label>
              {/* CB:BEGIN manufacturer-select */}
              <select
                className="form-control"
                value={materialManufacturer}
                onChange={(e) => setMaterialManufacturer(e.target.value)}
              >
                <option value="">Select manufacturer…</option>
                <option value="GAF">GAF</option>
                <option value="Owens Corning">Owens Corning</option>
                <option value="CertainTeed">CertainTeed</option>
                <option value="Atlas Roofing">Atlas Roofing</option>
                <option value="IKO">IKO</option>
                <option value="Tamko">Tamko</option>
                <option value="Malarkey">Malarkey</option>
                <option value="Other">Other</option>
              </select>
              {/* CB:END manufacturer-select */}
            </div>
            <div className="form-group">
              <label>Product Line <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                className="form-control"
                type="text"
                placeholder="e.g., Timberline HDZ"
                value={materialProductLine}
                onChange={(e) => setMaterialProductLine(e.target.value)}
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label>Product Specification URL <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              className="form-control"
              type="url"
              placeholder="https://www.gaf.com/..."
              value={materialSpecUrl}
              onChange={(e) => setMaterialSpecUrl(e.target.value)}
            />
          </div>
          {/* CB:BEGIN structured-color-list-ui */}
          <div className="form-group">
            <label>Available Color Options <span style={{ color: '#ef4444' }}>*</span></label>
            <div style={{ marginBottom: 8, fontSize: 13, color: '#6b7280' }}>
              Add the specific colors the homeowner can choose from. They will select from this list.
            </div>
            {colorList.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 8px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {colorList.map((color, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f3f4f6', borderRadius: 4, padding: '3px 8px', fontSize: 13 }}>
                    <span>{color}</span>
                    <button
                      type="button"
                      onClick={() => setColorList((prev) => prev.filter((_, idx) => idx !== i))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontWeight: 700, lineHeight: 1, padding: '0 2px' }}
                      aria-label={`Remove ${color}`}
                    >×</button>
                  </li>
                ))}
              </ul>
            )}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                className="form-control"
                type="text"
                placeholder="e.g. Charcoal"
                value={newColorInput}
                onChange={(e) => setNewColorInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const trimmed = newColorInput.trim();
                    if (trimmed && !colorList.includes(trimmed)) setColorList((prev) => [...prev, trimmed]);
                    setNewColorInput('');
                  }
                }}
                style={{ flex: 1, minWidth: 0 }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                style={{ flexShrink: 0, padding: '6px 10px', fontSize: 13, height: 36, width: 52, whiteSpace: 'nowrap' }}
                onClick={() => {
                  const trimmed = newColorInput.trim();
                  if (trimmed && !colorList.includes(trimmed)) setColorList((prev) => [...prev, trimmed]);
                  setNewColorInput('');
                }}
              >
                Add
              </button>
            </div>
            {colorOptions.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 13, color: '#059669' }}>
                ✓ {colorOptions.length} color{colorOptions.length !== 1 ? 's' : ''} added
              </div>
            )}
          </div>
          {/* CB:END structured-color-list-ui */}
        </div>
        {/* CB:END material-specification-fields */}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          <div className="form-group">
            <label>Material Warranty (years)</label>
            <input
              className="form-control"
              type="number"
              min={0}
              placeholder="e.g., 30"
              value={warrantyMaterialYears === '' ? '' : warrantyMaterialYears}
              onChange={(e) => setWarrantyMaterialYears(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>
          <div className="form-group">
            <label>Workmanship Warranty (years)</label>
            <input
              className="form-control"
              type="number"
              min={0}
              placeholder="e.g., 10"
              value={warrantyLaborYears === '' ? '' : warrantyLaborYears}
              onChange={(e) => setWarrantyLaborYears(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          <div className="form-group">
            <label>Manufacturer warranty (optional description)</label>
            <input className="form-control" type="text" value={manufacturerWarranty} onChange={(e) => setManufacturerWarranty(e.target.value)} placeholder="e.g., 30-year transferable" />
          </div>
          <div className="form-group">
            <label>Workmanship warranty (optional description)</label>
            <input className="form-control" type="text" value={workmanshipWarrantyText} onChange={(e) => setWorkmanshipWarrantyText(e.target.value)} placeholder="e.g., 10-year workmanship" />
          </div>
        </div>
      </div>

      {/* Scope of Work & Project Notes — single section, no duplicate labels */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Scope of Work & Project Notes</h4>
        <div className="form-group">
          <label>Scope of Work</label>
          <textarea className="form-control" rows={3} placeholder="Describe scope of work (optional)" value={scopeNotes} onChange={(e) => setScopeNotes(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Additional Information & Project Notes</label>
          <textarea className="form-control" rows={3} maxLength={500} placeholder="Max 500 characters" value={additionalNotes} onChange={(e) => setAdditionalNotes(e.target.value)} />
        </div>
      </div>

      {/* Final Bid Summary */}
      <div style={{ background: '#f0f9ff', borderRadius: 8, padding: 20, marginBottom: 20, position: 'relative' }}>
        <h4 style={{ color: '#1e40af', marginBottom: 12 }}>Final Bid Summary</h4>
        {onShowPlatformFeeInfo && (
          <button
            type="button"
            onClick={onShowPlatformFeeInfo}
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              width: 14,
              height: 14,
              backgroundColor: '#e5e7eb',
              color: '#6b7280',
              fontSize: 10,
              fontWeight: 600,
            }}
            aria-label="Platform fee information"
          >
            i
          </button>
        )}
        <div style={{ background: 'white', borderRadius: 8, padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span>Your Bid Amount</span>
            <span>${effectiveBid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span>Taxes and Fees</span>
            <span>${platformFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 18, borderTop: '1px solid #e5e7eb', paddingTop: 8 }}>
            <span>Customer Total</span>
            <span style={{ color: '#059669' }}>${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 24 }}>
        <button
          aria-label="Submit Bid"
          className="btn btn-success"
          style={{ padding: '12px 24px' }}
          disabled={submitting || disabled || subtotalCents <= 0}
          onClick={handleSubmit}
        >
          {submitting ? 'Submitting…' : 'Submit Bid'}
        </button>
        <button
          aria-label="Save Draft"
          className="btn btn-secondary"
          style={{ padding: '12px 24px' }}
          disabled={savingDraft}
          onClick={handleSaveDraft}
        >
          {savingDraft ? 'Saving…' : 'Save Draft'}
        </button>
      </div>
    </div>
  );
};

export default ContractorQuoteBidForm;
// CB:END contractor-quote-bid-form
